// skills/admin/adminUI.js
// Admin panel UI rendering and interaction

import { adminPanel } from './admin.js';
import { SPEED_PRESETS, XP_PRESETS, TIME_SKIP_PRESETS, ACTION_COMPLETE_PRESETS, OFFLINE_PRESETS, MAX_VIRTUAL_LEVEL, CORRUPTION_CHANCE_PRESETS } from './adminData.js';
import { SKILLS } from '../../game/constants.js';
import { gameState } from '../../game/gameState.js';
import logger from '../../utils/logger.js';
import { formatNumber } from '../../utils/format.js';
import { getAllItems, ITEM_CATEGORIES, getItemIcon } from '../../data/items/index.js';
import { skillRegistry } from '../../core/skillRegistry.js';
import { toastSuccess } from '../../ui/toast.js';

// Category icons mapping
const CATEGORY_ICONS = {
    'all': '📦',
    'ore': '🪨',
    'log': '🪵',
    'fish_raw': '🐟',
    'fish_cooked': '🍣',
    'bar': '🧱',
    'crop': '🥕',
    'seed': '🌱',
    'tree_seed': '🌲',
    'currency': getItemIcon('coins'),
    'misc': '📦',
    'arrowhead': '🔺',
    'arrow_shaft': '🥢',
    'arrow': '🏹',
    'bow': '🎯',
    'ichor': '💧',
    'glyph': '✦',
    'herb_seed': '🌿',
    'herb_grimy': '🍃',
    'herb_clean': '✨',
    'vial': '🧪',
    'potion': '🧴',
    'gem': '💎',
    'band': '💍',
    'chain': '⛓️',
    'sigil': '🔮',
    'glyph_core': '🔮',
    'staff': '🪄',
    'pet_food': '🦴',
    'pet_material': '🧵',
    'hide': '🐂',
    'armor': '🛡️',
    'equipment': '⚔️',
    'consumable': '🍖',
    'monster_drop': '👹'
};

// Get category icon
function getCategoryIcon(category) {
    return CATEGORY_ICONS[category] || '📦';
}

// Import skill data for dropdowns
import { ROCKS, PICKAXES } from '../mining/miningData.js';
import { TREES, AXES } from '../woodcutting/woodcuttingData.js';
import { FISH, RODS } from '../fishing/fishingData.js';
import { TARGETS, GLOVES } from '../pilfering/pilferingData.js';
import { CROPS, TREES as FARMING_TREES, GRASS_PATCH, TOOLS as FARMING_TOOLS } from '../farming/farmingData.js';
import { LOGS, TOOLS as PYRE_TOOLS } from '../pyre/pyreData.js';
import { FOOD, TOOLS as COOKING_TOOLS } from '../cooking/cookingData.js';
import { BARS, TOOLS as SMITHING_TOOLS } from '../smithing/smithingData.js';
import { JEWELRY, ALL_ARMOR, TOOLS as CRAFTING_TOOLS } from '../crafting/craftingData.js';
import { BLUEPRINTS, TOOLS as ARCHITECTING_TOOLS, MILESTONES } from '../architecting/architectingData.js';
import { QUESTS, getTotalQuestCount } from '../../data/quests/questData.js';

// UI state tracking to preserve selections across re-renders
let uiState = {
    selectedSkill: 'mining',
    customSpeedValue: null,
    customXpValue: null,
    selectedNode: null,
    selectedTool: null,
    selectedItemCategory: 'all',
    selectedItem: null,
    itemSearchFilter: ''
};

// Save UI state before re-render
function saveUIState() {
    const skillSelect = document.getElementById('admin-skill-select');
    const speedInput = document.getElementById('admin-speed-input');
    const xpInput = document.getElementById('admin-xp-mult-input');
    const nodeSelect = document.getElementById('admin-node-id');
    const toolSelect = document.getElementById('admin-tool-id');
    const itemCategorySelect = document.getElementById('admin-item-category');
    const itemSelect = document.getElementById('admin-item-select');
    const itemSearch = document.getElementById('admin-item-search');

    if (skillSelect) uiState.selectedSkill = skillSelect.value;
    if (speedInput) uiState.customSpeedValue = speedInput.value;
    if (xpInput) uiState.customXpValue = xpInput.value;
    if (nodeSelect) uiState.selectedNode = nodeSelect.value;
    if (toolSelect) uiState.selectedTool = toolSelect.value;
    if (itemCategorySelect) uiState.selectedItemCategory = itemCategorySelect.value;
    if (itemSelect) uiState.selectedItem = itemSelect.value;
    if (itemSearch) uiState.itemSearchFilter = itemSearch.value;
}

// Restore UI state after re-render
function restoreUIState() {
    const skillSelect = document.getElementById('admin-skill-select');
    const speedInput = document.getElementById('admin-speed-input');
    const xpInput = document.getElementById('admin-xp-mult-input');
    const nodeSelect = document.getElementById('admin-node-id');
    const toolSelect = document.getElementById('admin-tool-id');
    const itemCategorySelect = document.getElementById('admin-item-category');
    const itemSelect = document.getElementById('admin-item-select');
    const itemSearch = document.getElementById('admin-item-search');

    if (skillSelect && uiState.selectedSkill) skillSelect.value = uiState.selectedSkill;
    if (speedInput && uiState.customSpeedValue) speedInput.value = uiState.customSpeedValue;
    if (xpInput && uiState.customXpValue) xpInput.value = uiState.customXpValue;
    if (nodeSelect && uiState.selectedNode) nodeSelect.value = uiState.selectedNode;
    if (toolSelect && uiState.selectedTool) toolSelect.value = uiState.selectedTool;
    if (itemCategorySelect && uiState.selectedItemCategory) itemCategorySelect.value = uiState.selectedItemCategory;
    if (itemSelect && uiState.selectedItem) itemSelect.value = uiState.selectedItem;
    if (itemSearch) itemSearch.value = uiState.itemSearchFilter || '';
}

// Refresh admin UI while preserving state
function refreshAdminUI() {
    saveUIState();
    renderAdminUI();
    restoreUIState();
}

// Get nodes/resources for a skill
function getNodesForSkill(skillId) {
    if (skillId === 'farming') {
        // Farming has grass patch, crops, and trees
        return [GRASS_PATCH, ...Object.values(CROPS), ...Object.values(FARMING_TREES)];
    }

    const nodeMap = {
        'mining': ROCKS,
        'woodcutting': TREES,
        'fishing': FISH,
        'pilfering': TARGETS,
        'pyre': LOGS,
        'cooking': FOOD,
        'smithing': BARS,
        'crafting': { ...JEWELRY, ...ALL_ARMOR },
        'architecting': BLUEPRINTS
    };
    return Object.values(nodeMap[skillId] || {});
}

// Get tools for a skill
function getToolsForSkill(skillId) {
    const toolMap = {
        'mining': PICKAXES,
        'woodcutting': AXES,
        'fishing': RODS,
        'pilfering': GLOVES,
        'farming': FARMING_TOOLS,
        'pyre': PYRE_TOOLS,
        'cooking': COOKING_TOOLS,
        'smithing': SMITHING_TOOLS,
        'crafting': CRAFTING_TOOLS,
        'architecting': ARCHITECTING_TOOLS
    };
    return Object.values(toolMap[skillId] || []);
}

export function renderAdminUI() {
    const container = document.getElementById('skill-content');
    const state = adminPanel.getState();

    container.innerHTML = `
    <div class="admin-interface">
      <div class="admin-header">
        <h2>Admin Panel</h2>
        ${state.isActive ? '<span class="override-indicator">OVERRIDES ACTIVE</span>' : ''}
      </div>

      <div class="admin-content">
        ${renderLevelSection()}
        ${renderGoldSection()}
        ${renderQuestSection()}
        ${renderItemSpawnSection()}
        ${renderTimeSkipSection()}
        ${renderCombatGearSection()}
        ${renderFarmPlotSection()}
        ${renderBloomholdSection()}
        ${renderPilferingGuildSection()}
        ${renderPetContractSection()}
        ${renderBountyContractSection()}
        ${renderCorruptionSection()}
        ${renderOfflineSimulationSection()}
        ${renderSpeedSection()}
        ${renderXpSection()}
        ${renderToolSection()}
        ${renderMilestoneUnlocksSection()}
        ${renderResetSection()}
      </div>
    </div>
  `;
}

function renderLevelSection() {
    const skills = adminPanel.getSkillsList();
    const selectedSkill = uiState.selectedSkill || 'mining';

    // Get skill icons from registry
    const skillOptions = skills.map(s => {
        const skillConfig = skillRegistry.get(s.id);
        const icon = skillConfig?.icon || '📊';
        return `<option value="${s.id}" ${s.id === selectedSkill ? 'selected' : ''}>${icon} ${s.name}</option>`;
    }).join('');

    // Get selected skill icon for display
    const selectedSkillConfig = skillRegistry.get(selectedSkill);
    const selectedIcon = selectedSkillConfig?.icon || '📊';

    // Check if virtual levels is enabled to determine max level
    const virtualEnabled = gameState.settings.virtualLevelsEnabled;
    const maxLevel = virtualEnabled ? MAX_VIRTUAL_LEVEL : 99;

    return `
    <div class="admin-section">
      <h3>📊 Level / XP Manipulation</h3>

      <div class="admin-row">
        <label class="admin-label">Skill:</label>
        <select id="admin-skill-select" class="admin-select" onchange="window.adminUI.onSkillChange()">
          ${skillOptions}
        </select>
      </div>

      <div class="admin-row">
        <label class="admin-label">Level:</label>
        <div class="admin-btn-group">
          <button class="admin-btn" onclick="window.adminUI.addLevels(-10)">-10</button>
          <button class="admin-btn" onclick="window.adminUI.addLevels(-1)">-1</button>
          <input type="number" id="admin-level-input" class="admin-input" min="1" max="${maxLevel}" value="1" />
          <button class="admin-btn" onclick="window.adminUI.setLevel()">Set</button>
          <button class="admin-btn" onclick="window.adminUI.addLevels(1)">+1</button>
          <button class="admin-btn" onclick="window.adminUI.addLevels(10)">+10</button>
        </div>
      </div>

      <div class="admin-row">
        <label class="admin-label">Presets:</label>
        <div class="admin-btn-group">
          <button class="admin-btn" onclick="window.adminUI.setLevelTo(99)">Set 99</button>
          ${virtualEnabled ? '<button class="admin-btn" onclick="window.adminUI.setLevelTo(120)">Set 120</button>' : ''}
        </div>
      </div>

      <div class="admin-row">
        <label class="admin-label">XP:</label>
        <div class="admin-btn-group">
          <button class="admin-btn" onclick="window.adminUI.addXP(-1000)">-1K</button>
          <button class="admin-btn" onclick="window.adminUI.addXP(-100)">-100</button>
          <input type="number" id="admin-xp-input" class="admin-input" min="0" value="1000" />
          <button class="admin-btn" onclick="window.adminUI.addXPCustom()">Add</button>
          <button class="admin-btn" onclick="window.adminUI.addXP(100)">+100</button>
          <button class="admin-btn" onclick="window.adminUI.addXP(1000)">+1K</button>
        </div>
      </div>
    </div>
  `;
}

function renderGoldSection() {
    const currentGold = adminPanel.getGold();

    return `
    <div class="admin-section">
      <h3>${getItemIcon('coins')} Gold / Currency</h3>
      <p class="admin-hint">Current Gold: <strong>${getItemIcon('coins')} ${formatNumber(currentGold)}</strong></p>

      <div class="admin-row">
        <label class="admin-label">Add Gold:</label>
        <div class="preset-grid">
          <button class="admin-btn preset-btn" onclick="window.adminUI.addGold(100)">+100</button>
          <button class="admin-btn preset-btn" onclick="window.adminUI.addGold(1000)">+1K</button>
          <button class="admin-btn preset-btn" onclick="window.adminUI.addGold(10000)">+10K</button>
          <button class="admin-btn preset-btn" onclick="window.adminUI.addGold(100000)">+100K</button>
          <button class="admin-btn preset-btn" onclick="window.adminUI.addGold(1000000)">+1M</button>
        </div>
      </div>

      <div class="admin-row">
        <label class="admin-label">Custom:</label>
        <div class="admin-btn-group">
          <input type="number" id="admin-gold-input" class="admin-input" min="0" value="10000" />
          <button class="admin-btn" onclick="window.adminUI.addGoldCustom()">Add</button>
          <button class="admin-btn" onclick="window.adminUI.setGoldCustom()">Set</button>
        </div>
      </div>
    </div>
  `;
}

function renderQuestSection() {
    const totalQuests = getTotalQuestCount();
    const completedQuests = gameState.getCompletedQuests();
    const completedCount = completedQuests.length;

    // Build quest options - group by act
    const questOptions = [];
    for (let i = 1; i <= totalQuests; i++) {
        const quest = QUESTS[i];
        if (quest) {
            const isCompleted = completedQuests.includes(i);
            const status = isCompleted ? '✓' : '';
            questOptions.push(`<option value="${i}" ${isCompleted ? 'style="color: #4ade80;"' : ''}>${status} ${i}. ${quest.name}</option>`);
        }
    }

    return `
    <div class="admin-section">
      <h3>📜 Quest Management</h3>
      <p class="admin-hint">Completed: <strong>${completedCount}/${totalQuests}</strong> quests</p>

      <div class="admin-row">
        <label class="admin-label">Quest:</label>
        <select id="admin-quest-select" class="admin-select">
          ${questOptions.join('')}
        </select>
      </div>

      <div class="admin-row">
        <label class="admin-label">Actions:</label>
        <div class="admin-btn-group">
          <button class="admin-btn" onclick="window.adminUI.completeSelectedQuest()">Complete Quest</button>
          <button class="admin-btn" onclick="window.adminUI.completeAllQuests()">Complete All</button>
          <button class="admin-btn danger" onclick="window.adminUI.resetAllQuests()">Reset All</button>
        </div>
      </div>
    </div>
  `;
}

function renderItemSpawnSection() {
    const selectedCategory = uiState.selectedItemCategory || 'all';
    const allItems = getAllItems();

    // Build category options with icons
    const categories = ['all', ...Object.values(ITEM_CATEGORIES)];
    const categoryOptions = categories.map(cat => {
        const icon = getCategoryIcon(cat);
        const displayName = cat === 'all' ? 'All Items' : cat.replace(/_/g, ' ');
        return `<option value="${cat}" ${cat === selectedCategory ? 'selected' : ''}>${icon} ${displayName}</option>`;
    }).join('');

    // Filter items by category
    let filteredItems = selectedCategory === 'all'
        ? allItems
        : allItems.filter(item => item.category === selectedCategory);

    // Apply search filter
    const searchFilter = (uiState.itemSearchFilter || '').toLowerCase().trim();
    if (searchFilter) {
        filteredItems = filteredItems.filter(item =>
            item.name.toLowerCase().includes(searchFilter) ||
            item.id.toLowerCase().includes(searchFilter)
        );
    }

    // Sort items alphabetically by name
    filteredItems.sort((a, b) => a.name.localeCompare(b.name));

    // Build item options with icons (use category icon since <option> can't render HTML sprites)
    const itemOptions = filteredItems.map(item => {
        const icon = getCategoryIcon(item.category);
        return `<option value="${item.id}">${icon} ${item.name}</option>`;
    }).join('');

    // Get search filter value for the input field
    const searchValue = uiState.itemSearchFilter || '';

    return `
    <div class="admin-section">
      <h3>📦 Item Spawning</h3>
      <p class="admin-hint">Add items to inventory or bank for testing. ${allItems.length} items registered.</p>

      <div class="admin-row">
        <label class="admin-label">Category:</label>
        <select id="admin-item-category" class="admin-select" onchange="window.adminUI.onItemCategoryChange()">
          ${categoryOptions}
        </select>
      </div>

      <div class="admin-row">
        <label class="admin-label">Search:</label>
        <input type="text" id="admin-item-search" class="admin-input" style="width: 200px;" placeholder="Type to filter items..." value="${searchValue}" oninput="window.adminUI.onItemSearchChange()" />
      </div>

      <div class="admin-row">
        <label class="admin-label">Item:</label>
        <select id="admin-item-select" class="admin-select" style="flex: 2;">
          ${itemOptions}
        </select>
      </div>

      <div class="admin-row">
        <label class="admin-label">Quantity:</label>
        <div class="admin-btn-group">
          <input type="number" id="admin-item-qty" class="admin-input" min="1" value="10" />
          <button class="admin-btn" onclick="window.adminUI.addItemToInventory()">📥 Inventory</button>
          <button class="admin-btn" onclick="window.adminUI.addItemToBank()">🏦 Bank</button>
        </div>
      </div>

      <div class="admin-row">
        <label class="admin-label">Quick Add:</label>
        <div class="preset-grid">
          <button class="admin-btn preset-btn" onclick="window.adminUI.addItemQty(1)">+1</button>
          <button class="admin-btn preset-btn" onclick="window.adminUI.addItemQty(10)">+10</button>
          <button class="admin-btn preset-btn" onclick="window.adminUI.addItemQty(100)">+100</button>
          <button class="admin-btn preset-btn" onclick="window.adminUI.addItemQty(1000)">+1K</button>
        </div>
      </div>
    </div>
  `;
}

function renderTimeSkipSection() {
    const timeButtons = TIME_SKIP_PRESETS.map(m =>
        `<button class="admin-btn preset-btn" onclick="window.adminUI.skipTime(${m})">${m}m</button>`
    ).join('');

    const actionButtons = ACTION_COMPLETE_PRESETS.map(n =>
        `<button class="admin-btn preset-btn" onclick="window.adminUI.completeActions(${n})">${n}x</button>`
    ).join('');

    return `
    <div class="admin-section">
      <h3>⏳ Time Skip</h3>

      <div class="admin-row">
        <label class="admin-label">Skip Time:</label>
        <div class="preset-grid">
          ${timeButtons}
        </div>
      </div>

      <div class="admin-row">
        <label class="admin-label">Complete Actions:</label>
        <div class="preset-grid">
          ${actionButtons}
        </div>
      </div>
    </div>
  `;
}

function renderCombatGearSection() {
    return `
    <div class="admin-section">
      <h3>⚔️ Combat Gear Presets</h3>
      <p class="admin-hint">Equip full starter sets for testing combat at level 1.</p>

      <div class="admin-row">
        <label class="admin-label">Equip Set:</label>
        <div class="admin-btn-group">
          <button class="admin-btn" onclick="window.adminUI.equipStarterMelee()">⚔️ Melee</button>
          <button class="admin-btn" onclick="window.adminUI.equipStarterRanged()">🏹 Ranged</button>
          <button class="admin-btn" onclick="window.adminUI.equipStarterMagic()">🪄 Magic</button>
        </div>
      </div>
    </div>
  `;
}

function renderFarmPlotSection() {
    const summary = adminPanel.getFarmPlotsSummary();

    if (!summary) {
        return `
        <div class="admin-section">
          <h3>Farm Plot Controls</h3>
          <p class="admin-hint">Farm plots not initialized.</p>
        </div>
      `;
    }

    return `
    <div class="admin-section">
      <h3>🌾 Farm Plot Controls</h3>
      <p class="admin-hint">Manage passive farm plot growth. Empty: ${summary.empty} | Growing: ${summary.growing} | Ready: ${summary.ready} | Locked: ${summary.locked}</p>

      <div class="admin-row">
        <label class="admin-label">Skip Plot Time:</label>
        <div class="preset-grid">
          <button class="admin-btn preset-btn" onclick="window.adminUI.skipPlotTime(5)">5m</button>
          <button class="admin-btn preset-btn" onclick="window.adminUI.skipPlotTime(15)">15m</button>
          <button class="admin-btn preset-btn" onclick="window.adminUI.skipPlotTime(30)">30m</button>
          <button class="admin-btn preset-btn" onclick="window.adminUI.skipPlotTime(60)">1h</button>
          <button class="admin-btn preset-btn" onclick="window.adminUI.skipPlotTime(180)">3h</button>
          <button class="admin-btn preset-btn" onclick="window.adminUI.skipPlotTime(360)">6h</button>
        </div>
      </div>

      <div class="admin-row">
        <label class="admin-label">Custom:</label>
        <div class="admin-btn-group">
          <input type="number" id="admin-plot-time-input" class="admin-input" min="1" max="720" value="15" />
          <span class="admin-unit">min</span>
          <button class="admin-btn" onclick="window.adminUI.skipPlotTimeCustom()">Skip</button>
        </div>
      </div>

      <div class="admin-row">
        <label class="admin-label">Actions:</label>
        <div class="admin-btn-group">
          <button class="admin-btn" onclick="window.adminUI.completeAllPlots()">Complete All</button>
          <button class="admin-btn" onclick="window.adminUI.clearAllPlots()">Clear All</button>
          <button class="admin-btn" onclick="window.adminUI.unlockAllPlots()">Unlock All</button>
          <button class="admin-btn" onclick="window.adminUI.resetPlotLocks()">Reset Locks</button>
        </div>
      </div>
    </div>
  `;
}

function renderBloomholdSection() {
    const summary = adminPanel.getBloomholdSummary();

    return `
    <div class="admin-section">
      <h3>🌸 BloomHold Controls</h3>
      <p class="admin-hint">Manage BloomHold seed vault. Status: ${summary.unlocked ? 'Unlocked' : 'Locked'} | Slots: ${summary.usedSlots}/${summary.maxSlots} | Items: ${summary.totalItems}</p>

      <div class="admin-row">
        <label class="admin-label">Unlock:</label>
        <div class="admin-btn-group">
          ${summary.unlocked
            ? `<button class="admin-btn" onclick="window.adminUI.lockBloomhold()">Lock BloomHold</button>`
            : `<button class="admin-btn" onclick="window.adminUI.unlockBloomhold()">Unlock BloomHold</button>`
          }
          <button class="admin-btn" onclick="window.adminUI.clearBloomhold()">Move All to Bank</button>
        </div>
      </div>
    </div>
  `;
}

function renderPilferingGuildSection() {
    const isUnlocked = gameState.isGuildUnlocked();
    const chestSize = gameState.getGuildChest().length;

    return `
    <div class="admin-section">
      <h3>🗡️ Pilfering Guild Controls</h3>
      <p class="admin-hint">Manage Pilfering Guild. Status: ${isUnlocked ? 'Unlocked' : 'Locked'} | Chest Items: ${chestSize}</p>

      <div class="admin-row">
        <label class="admin-label">Unlock:</label>
        <div class="admin-btn-group">
          ${isUnlocked
            ? `<button class="admin-btn" onclick="window.adminUI.lockPilferingGuild()">Lock Guild</button>`
            : `<button class="admin-btn" onclick="window.adminUI.unlockPilferingGuild()">Unlock Guild</button>`
          }
        </div>
      </div>
    </div>
  `;
}

function renderPetContractSection() {
    const summary = adminPanel.getPetContractSummary();
    const chestSummary = adminPanel.getLootChestSummary();

    return `
    <div class="admin-section">
      <h3>🐾 Husbandry Controls</h3>
      <p class="admin-hint">Pet contracts: ${summary.activeContracts} active | ${summary.idlePets} idle | Chest: ${chestSummary.itemTypes} types (${chestSummary.totalItems} items)</p>

      <div class="admin-row">
        <label class="admin-label">Pets:</label>
        <div class="admin-btn-group">
          <button class="admin-btn" onclick="window.adminUI.unlockAllPets()">Unlock All</button>
          <button class="admin-btn danger" onclick="window.adminUI.lockAllPets()">Lock All</button>
        </div>
      </div>

      <div class="admin-row">
        <label class="admin-label">Pet Progress:</label>
        <div class="admin-btn-group">
          <button class="admin-btn" onclick="window.adminUI.maxAllPetFeeds()">Max All Feeds</button>
          <button class="admin-btn danger" onclick="window.adminUI.resetAllPetFeeds()">Reset Feeds</button>
          <button class="admin-btn danger" onclick="window.adminUI.resetAllPetTiers()">Reset Tiers</button>
        </div>
      </div>

      <div class="admin-row">
        <label class="admin-label">Loot Chest:</label>
        <div class="admin-btn-group">
          <button class="admin-btn" onclick="window.adminUI.fillLootChest()">Fill Chest</button>
          <button class="admin-btn danger" onclick="window.adminUI.clearLootChest()">Clear Chest</button>
        </div>
      </div>

      <div class="admin-row">
        <label class="admin-label">Skip Contract Time:</label>
        <div class="preset-grid">
          <button class="admin-btn preset-btn" onclick="window.adminUI.skipContractTime(5)">5m</button>
          <button class="admin-btn preset-btn" onclick="window.adminUI.skipContractTime(15)">15m</button>
          <button class="admin-btn preset-btn" onclick="window.adminUI.skipContractTime(30)">30m</button>
          <button class="admin-btn preset-btn" onclick="window.adminUI.skipContractTime(60)">1h</button>
          <button class="admin-btn preset-btn" onclick="window.adminUI.skipContractTime(180)">3h</button>
          <button class="admin-btn preset-btn" onclick="window.adminUI.skipContractTime(360)">6h</button>
        </div>
      </div>

      <div class="admin-row">
        <label class="admin-label">Custom:</label>
        <div class="admin-btn-group">
          <input type="number" id="admin-contract-time-input" class="admin-input" min="1" max="720" value="30" />
          <span class="admin-unit">min</span>
          <button class="admin-btn" onclick="window.adminUI.skipContractTimeCustom()">Skip</button>
        </div>
      </div>

      <div class="admin-row">
        <label class="admin-label">Pet Contracts:</label>
        <div class="admin-btn-group">
          <button class="admin-btn" onclick="window.adminUI.completeAllContracts()">Complete All</button>
        </div>
      </div>
    </div>
  `;
}

function renderBountyContractSection() {
    const summary = adminPanel.getBountyContractSummary();

    return `
    <div class="admin-section">
      <h3>📜 Bounty Controls</h3>
      <p class="admin-hint">Active bounty contracts: ${summary.activeCount}</p>

      <div class="admin-row">
        <label class="admin-label">Requirements:</label>
        <div class="admin-btn-group">
          <button class="admin-btn" onclick="window.adminUI.spawnBountyRequirements()">Spawn Required Items</button>
        </div>
      </div>

      <div class="admin-row">
        <label class="admin-label">Actions:</label>
        <div class="admin-btn-group">
          <button class="admin-btn danger" onclick="window.adminUI.clearBountyContracts()">Clear All Contracts</button>
        </div>
      </div>
    </div>
  `;
}

function renderCorruptionSection() {
    const corruptionInfo = adminPanel.getCorruptionInfo();
    const config = adminPanel.getCorruptionConfig();
    const currentProcChance = adminPanel.getCorruptionProcChance();

    const statusText = corruptionInfo.active
        ? `Active on ${corruptionInfo.skillId} (${corruptionInfo.remainingSeconds}s remaining)`
        : 'Inactive';

    // Build proc chance buttons
    const procChanceButtons = CORRUPTION_CHANCE_PRESETS.map(preset => {
        const isActive = (preset.value === null && currentProcChance === null) ||
                        (preset.value !== null && currentProcChance === preset.value);
        return `<button class="admin-btn preset-btn ${isActive ? 'active' : ''}"
                        onclick="window.adminUI.setCorruptionProcChance(${preset.value === null ? 'null' : preset.value})">${preset.label}</button>`;
    }).join('');

    // Show current proc chance info
    const currentChanceText = currentProcChance !== null
        ? `<span class="override-active">(Override: ${(currentProcChance * 100).toFixed(0)}%)</span>`
        : `(Default: 0.2% / 2% at mastery)`;

    return `
    <div class="admin-section">
      <h3>⚡ Corruption Testing</h3>
      <p class="admin-hint">Test corruption mechanic. Status: ${statusText}</p>
      <p class="admin-hint">Buffs: ${config.SPEED_MULTIPLIER}x speed, ${config.LOOT_MULTIPLIER}x loot, ${Math.round(config.RESOURCE_SAVE_CHANCE * 100)}% resource save, free gold costs</p>

      <div class="admin-row">
        <label class="admin-label">Proc Chance:</label>
        <div class="preset-grid">
          ${procChanceButtons}
        </div>
      </div>
      <p class="admin-hint" style="margin-top: -8px;">Natural proc chance ${currentChanceText}</p>

      <div class="admin-row">
        <label class="admin-label">Force Trigger:</label>
        <div class="preset-grid">
          <button class="admin-btn preset-btn" onclick="window.adminUI.triggerCorruption(30)">30s</button>
          <button class="admin-btn preset-btn" onclick="window.adminUI.triggerCorruption(60)">60s</button>
          <button class="admin-btn preset-btn" onclick="window.adminUI.triggerCorruption(120)">2m</button>
          <button class="admin-btn preset-btn" onclick="window.adminUI.triggerCorruption(300)">5m (Max)</button>
        </div>
      </div>

      <div class="admin-row">
        <label class="admin-label">Control:</label>
        <div class="admin-btn-group">
          <button class="admin-btn danger" onclick="window.adminUI.endCorruption()">End Corruption</button>
        </div>
      </div>
    </div>
  `;
}

function renderOfflineSimulationSection() {
    const presetButtons = OFFLINE_PRESETS.map(p =>
        `<button class="admin-btn preset-btn" onclick="window.adminUI.simulateOffline(${p.minutes})">${p.label}</button>`
    ).join('');

    return `
    <div class="admin-section">
      <h3>💤 Offline Simulation</h3>
      <p class="admin-hint">Simulate being offline to test offline progress. Must have an action running.</p>

      <div class="admin-row">
        <label class="admin-label">Presets:</label>
        <div class="preset-grid">
          ${presetButtons}
        </div>
      </div>

      <div class="admin-row">
        <label class="admin-label">Custom:</label>
        <div class="admin-btn-group">
          <input type="number" id="admin-offline-input" class="admin-input" min="1" max="1440" value="60" />
          <span class="admin-unit">min</span>
          <button class="admin-btn" onclick="window.adminUI.simulateOfflineCustom()">Simulate</button>
        </div>
      </div>
    </div>
  `;
}

function renderSpeedSection() {
    const state = adminPanel.getState();
    const globalSpeed = state.global.speedMultiplier;

    const speedButtons = SPEED_PRESETS.map(s =>
        `<button class="admin-btn preset-btn ${globalSpeed === s ? 'active' : ''}"
                 onclick="window.adminUI.setGlobalSpeed(${s})">${s}x</button>`
    ).join('');

    return `
    <div class="admin-section">
      <h3>⚡ Speed Multiplier</h3>

      <div class="admin-row">
        <label class="admin-label">Global Speed:</label>
        <div class="preset-grid">
          ${speedButtons}
        </div>
      </div>

      <div class="admin-row">
        <label class="admin-label">Custom:</label>
        <div class="admin-btn-group">
          <input type="number" id="admin-speed-input" class="admin-input" min="1" max="50" value="${globalSpeed}" />
          <button class="admin-btn" onclick="window.adminUI.setCustomSpeed()">Set</button>
        </div>
      </div>
    </div>
  `;
}

function renderXpSection() {
    const state = adminPanel.getState();
    const globalXp = state.global.xpMultiplier;
    const selectedSkill = uiState.selectedSkill || 'mining';
    const nodes = getNodesForSkill(selectedSkill);

    const xpButtons = XP_PRESETS.map(x =>
        `<button class="admin-btn preset-btn ${globalXp === x ? 'active' : ''}"
                 onclick="window.adminUI.setGlobalXpMultiplier(${x})">${x}x</button>`
    ).join('');

    const nodeOptions = nodes.map(n =>
        `<option value="${n.id}">${n.name} (${n.xp} XP)</option>`
    ).join('');

    // Build active node overrides display
    const nodeOverrides = Object.entries(state.nodeXpOverrides);
    let overridesHtml = '';
    if (nodeOverrides.length > 0) {
        const badges = nodeOverrides.map(([key, xp]) =>
            `<span class="override-badge" onclick="window.adminUI.removeNodeOverride('${key}')">${key}: ${xp} XP <span class="remove-x">×</span></span>`
        ).join('');
        overridesHtml = `<div class="active-overrides"><label class="admin-label">Active:</label><div class="override-list">${badges}</div></div>`;
    }

    return `
    <div class="admin-section">
      <h3>✨ XP Multiplier</h3>

      <div class="admin-row">
        <label class="admin-label">Global XP:</label>
        <div class="preset-grid">
          ${xpButtons}
        </div>
      </div>

      <div class="admin-row">
        <label class="admin-label">Custom:</label>
        <div class="admin-btn-group">
          <input type="number" id="admin-xp-mult-input" class="admin-input" min="0.1" max="100000" step="0.1" value="${globalXp}" />
          <button class="admin-btn" onclick="window.adminUI.setCustomXpMultiplier()">Set</button>
        </div>
      </div>

      <div class="admin-subsection">
        <h4>Node XP Override</h4>
        <div class="admin-row">
          <select id="admin-node-id" class="admin-select">
            ${nodeOptions}
          </select>
          <input type="number" id="admin-node-xp" class="admin-input" min="0" value="100" />
          <button class="admin-btn" onclick="window.adminUI.setNodeXpOverride()">Set</button>
          <button class="admin-btn" onclick="window.adminUI.clearNodeXpOverride()">Clear</button>
        </div>
        ${overridesHtml}
      </div>
    </div>
  `;
}

function renderToolSection() {
    const state = adminPanel.getState();
    const selectedSkill = uiState.selectedSkill || 'mining';
    const tools = getToolsForSkill(selectedSkill);

    let toolOptions = '';
    if (tools.length > 0) {
        toolOptions = tools.map(t =>
            `<option value="${t.id}">${t.name} (${t.speedMultiplier}x)</option>`
        ).join('');
    } else {
        toolOptions = '<option value="">No tools for this skill</option>';
    }

    // Build active tool overrides display
    const toolOverrides = Object.entries(state.toolSpeedOverrides);
    let overridesHtml = '';
    if (toolOverrides.length > 0) {
        const badges = toolOverrides.map(([toolId, speed]) =>
            `<span class="override-badge" onclick="window.adminUI.removeToolOverride('${toolId}')">${toolId}: ${speed}x <span class="remove-x">×</span></span>`
        ).join('');
        overridesHtml = `<div class="active-overrides"><label class="admin-label">Active:</label><div class="override-list">${badges}</div></div>`;
    }

    return `
    <div class="admin-section">
      <h3>🔧 Tool Speed Override</h3>

      <div class="admin-row">
        <select id="admin-tool-id" class="admin-select">
          ${toolOptions}
        </select>
        <input type="number" id="admin-tool-speed" class="admin-input" min="0.1" step="0.1" value="5" />
        <button class="admin-btn" onclick="window.adminUI.setToolSpeedOverride()">Set</button>
        <button class="admin-btn" onclick="window.adminUI.clearToolSpeedOverride()">Clear</button>
      </div>
      ${overridesHtml}
    </div>
  `;
}

function renderMilestoneUnlocksSection() {
    // Helper to check effect type (handles arrays and single objects)
    const hasEffectType = (milestone, type) => {
        if (Array.isArray(milestone.effect)) {
            return milestone.effect.some(e => e.type === type);
        }
        return milestone.effect?.type === type;
    };

    // Group milestones by effect type for display
    const unlockMilestones = Object.values(MILESTONES).filter(m => hasEffectType(m, 'unlock'));
    const storageMilestones = Object.values(MILESTONES).filter(m => hasEffectType(m, 'storage'));
    const speedMilestones = Object.values(MILESTONES).filter(m => hasEffectType(m, 'speed') && !hasEffectType(m, 'stack'));
    const stackMilestones = Object.values(MILESTONES).filter(m => hasEffectType(m, 'stack'));
    const pilferingTierMilestones = Object.values(MILESTONES).filter(m => hasEffectType(m, 'pilferingTier'));
    const husbandryTierMilestones = Object.values(MILESTONES).filter(m => hasEffectType(m, 'husbandryTier'));

    const renderMilestoneRow = (milestone) => {
        const isCompleted = gameState.hasMilestone(milestone.id);
        return `
            <div class="milestone-toggle-row ${isCompleted ? 'completed' : ''}">
                <span class="milestone-icon">${milestone.icon}</span>
                <span class="milestone-name">${milestone.name}</span>
                <span class="milestone-effect">${milestone.description}</span>
                <button class="admin-btn small ${isCompleted ? 'active' : ''}"
                        onclick="window.adminUI.toggleMilestone('${milestone.id}')">
                    ${isCompleted ? 'Remove' : 'Unlock'}
                </button>
            </div>
        `;
    };

    return `
    <div class="admin-section">
      <h3>🏗️ Milestone Unlocks</h3>

      <div class="milestone-category">
        <h4>Feature Unlocks</h4>
        ${unlockMilestones.map(renderMilestoneRow).join('')}
      </div>

      <div class="milestone-category">
        <h4>Storage Upgrades</h4>
        ${storageMilestones.map(renderMilestoneRow).join('')}
      </div>

      <div class="milestone-category">
        <h4>Stack + Speed Bonuses</h4>
        ${stackMilestones.map(renderMilestoneRow).join('')}
      </div>

      ${speedMilestones.length > 0 ? `
      <div class="milestone-category">
        <h4>Speed Bonuses</h4>
        ${speedMilestones.map(renderMilestoneRow).join('')}
      </div>
      ` : ''}

      <div class="milestone-category">
        <h4>🕸️ Pilfering Tier Upgrades</h4>
        ${pilferingTierMilestones.map(renderMilestoneRow).join('')}
      </div>

      <div class="milestone-category">
        <h4>🐾 Husbandry Tier Upgrades</h4>
        ${husbandryTierMilestones.map(renderMilestoneRow).join('')}
      </div>

      <div class="admin-row">
        <div class="admin-btn-group">
          <button class="admin-btn" onclick="window.adminUI.unlockAllMilestones()">Unlock All</button>
          <button class="admin-btn" onclick="window.adminUI.resetAllMilestones()">Reset All</button>
        </div>
      </div>
    </div>
  `;
}

function renderResetSection() {
    const selectedSkill = uiState.selectedSkill || 'mining';
    const purchasedTools = gameState.getPurchasedTools(selectedSkill);
    const toolCount = purchasedTools.length;

    return `
    <div class="admin-section admin-reset-section">
      <h3>🔄 Reset</h3>

      <div class="admin-row">
        <label class="admin-label">Tool Purchases:</label>
        <div class="admin-btn-group">
          <button class="admin-btn" onclick="window.adminUI.resetSkillTools()">
            Reset ${selectedSkill} Tools (${toolCount})
          </button>
          <button class="admin-btn" onclick="window.adminUI.grantAllSkillTools()">
            Grant All ${selectedSkill} Tools
          </button>
        </div>
      </div>

      <div class="admin-row">
        <label class="admin-label">General:</label>
        <div class="admin-btn-group">
          <button class="admin-btn" onclick="window.adminUI.resetOverrides()">Reset Overrides</button>
          <button class="admin-btn danger" onclick="window.adminUI.confirmResetCharacter()">Reset Character</button>
        </div>
      </div>
    </div>
  `;
}

// UI Action Handlers
function getSelectedSkill() {
    const select = document.getElementById('admin-skill-select');
    return select ? select.value : SKILLS.MINING;
}

function addLevels(levels) {
    const skillId = getSelectedSkill();
    const result = adminPanel.addLevels(skillId, levels);
    if (result.success) {
        logger.debug(`[ADMIN UI] Added ${levels} levels to ${skillId}`);
        refreshAdminUI();
    }
}

function setLevel() {
    const skillId = getSelectedSkill();
    const input = document.getElementById('admin-level-input');
    const level = parseInt(input.value, 10);
    if (!isNaN(level)) {
        const result = adminPanel.setLevel(skillId, level);
        if (result.success) {
            logger.debug(`[ADMIN UI] Set ${skillId} to level ${level}`);
            refreshAdminUI();
        }
    }
}

function setLevelTo(level) {
    const skillId = getSelectedSkill();
    const result = adminPanel.setLevel(skillId, level);
    if (result.success) {
        logger.debug(`[ADMIN UI] Set ${skillId} to level ${level}`);
        refreshAdminUI();
    }
}

function addXP(amount) {
    const skillId = getSelectedSkill();
    const result = adminPanel.addXP(skillId, amount);
    if (result.success) {
        logger.debug(`[ADMIN UI] Added ${amount} XP to ${skillId}`);
        refreshAdminUI();
    }
}

function addXPCustom() {
    const input = document.getElementById('admin-xp-input');
    const amount = parseInt(input.value, 10);
    if (!isNaN(amount)) {
        addXP(amount);
    }
}

function skipTime(minutes) {
    const result = adminPanel.skipTime(minutes);
    if (result.success) {
        logger.debug(`[ADMIN UI] Skipped ${minutes} minutes`);
    }
}

function completeActions(count) {
    const result = adminPanel.completeActions(count);
    if (result.success) {
        logger.debug(`[ADMIN UI] Completed ${count} actions`);
    } else {
        logger.warn('[ADMIN UI] Complete actions failed:', result.reason);
    }
}

async function simulateOffline(minutes) {
    const result = await adminPanel.simulateOfflineTime(minutes);
    if (!result.success) {
        logger.warn('[ADMIN UI] Simulate offline failed:', result.reason || 'Unknown error');
    }
}

async function simulateOfflineCustom() {
    const input = document.getElementById('admin-offline-input');
    const minutes = parseInt(input.value, 10);
    if (!isNaN(minutes) && minutes >= 1) {
        await simulateOffline(minutes);
    }
}

// Farm Plot Control Handlers
function skipPlotTime(minutes) {
    const result = adminPanel.skipPlotTime(minutes);
    if (result.success) {
        logger.debug(`[ADMIN UI] Skipped ${minutes} minutes for ${result.affectedPlots} plots`);
        refreshAdminUI();
    }
}

function skipPlotTimeCustom() {
    const input = document.getElementById('admin-plot-time-input');
    const minutes = parseInt(input.value, 10);
    if (!isNaN(minutes) && minutes >= 1) {
        skipPlotTime(minutes);
    }
}

function completeAllPlots() {
    const result = adminPanel.completeAllPlots();
    if (result.success) {
        logger.debug(`[ADMIN UI] Completed ${result.completedPlots} plots`);
        refreshAdminUI();
    }
}

function clearAllPlots() {
    const result = adminPanel.clearAllPlots();
    if (result.success) {
        logger.debug(`[ADMIN UI] Cleared ${result.clearedPlots} plots`);
        refreshAdminUI();
    }
}

function unlockAllPlots() {
    const result = adminPanel.unlockAllPlots();
    if (result.success) {
        logger.debug(`[ADMIN UI] Unlocked ${result.unlockedPlots} plots`);
        refreshAdminUI();
    }
}

function resetPlotLocks() {
    const result = adminPanel.resetPlotLocks();
    if (result.success) {
        logger.debug(`[ADMIN UI] Reset plot locks - ${result.lockedPlots} plots locked, ${result.clearedPlots} plots cleared`);
        refreshAdminUI();
    }
}

// BloomHold Control Handlers
function unlockBloomhold() {
    const result = adminPanel.unlockBloomhold();
    if (result.success) {
        logger.debug('[ADMIN UI] Unlocked BloomHold');
        refreshAdminUI();
    }
}

function lockBloomhold() {
    const result = adminPanel.lockBloomhold();
    if (result.success) {
        logger.debug('[ADMIN UI] Locked BloomHold');
        refreshAdminUI();
    }
}

function clearBloomhold() {
    const result = adminPanel.clearBloomhold();
    if (result.success) {
        logger.debug('[ADMIN UI] Cleared BloomHold');
        refreshAdminUI();
    }
}

// Pilfering Guild Control Handlers
function unlockPilferingGuild() {
    gameState.unlockGuild();
    logger.debug('[ADMIN UI] Unlocked Pilfering Guild');
    refreshAdminUI();
}

function lockPilferingGuild() {
    gameState.lockGuild();
    logger.debug('[ADMIN UI] Locked Pilfering Guild');
    refreshAdminUI();
}

// Pet Contract Handlers
function skipContractTime(minutes) {
    const result = adminPanel.skipContractTime(minutes);
    if (result.success) {
        logger.debug(`[ADMIN UI] Skipped ${minutes} minutes for ${result.affectedContracts} pet contracts`);
        refreshAdminUI();
    }
}

function skipContractTimeCustom() {
    const input = document.getElementById('admin-contract-time-input');
    const minutes = parseInt(input.value, 10);
    if (!isNaN(minutes) && minutes >= 1) {
        skipContractTime(minutes);
    }
}

function completeAllContracts() {
    const result = adminPanel.completeAllContracts();
    if (result.success) {
        logger.debug(`[ADMIN UI] Completed ${result.completedContracts} pet contracts`);
        refreshAdminUI();
    }
}

function unlockAllPets() {
    const result = adminPanel.unlockAllPets();
    if (result.success) {
        logger.debug(`[ADMIN UI] Unlocked ${result.unlockedCount} pets`);
        refreshAdminUI();
    }
}

function lockAllPets() {
    const result = adminPanel.lockAllPets();
    if (result.success) {
        logger.debug(`[ADMIN UI] Locked ${result.lockedCount} pets`);
        refreshAdminUI();
    }
}

function maxAllPetFeeds() {
    const pets = gameState.getPets();
    if (!pets) return;

    let maxedCount = 0;
    for (const pet of pets) {
        if (pet && pet.id) {
            const result = adminPanel.maxPetFeeds(pet.id);
            if (result.success) maxedCount++;
        }
    }
    logger.debug(`[ADMIN UI] Maxed feeds for ${maxedCount} pets`);
    refreshAdminUI();
}

function resetAllPetFeeds() {
    const result = adminPanel.resetAllPetFeeds();
    if (result.success) {
        logger.debug(`[ADMIN UI] Reset ${result.resetCount} pet feeds`);
        refreshAdminUI();
    }
}

function resetAllPetTiers() {
    const result = adminPanel.resetAllPetTiers();
    if (result.success) {
        logger.debug(`[ADMIN UI] Reset ${result.resetCount} pet tiers`);
        refreshAdminUI();
    }
}

function fillLootChest() {
    const result = adminPanel.fillLootChest();
    if (result.success) {
        logger.debug(`[ADMIN UI] Added ${result.addedCount} item types to loot chest`);
        refreshAdminUI();
    }
}

function clearLootChest() {
    const result = adminPanel.clearLootChest();
    if (result.success) {
        logger.debug(`[ADMIN UI] Cleared ${result.itemCount} items from loot chest`);
        refreshAdminUI();
    }
}

// Bounty Contract Handlers
function spawnBountyRequirements() {
    const result = adminPanel.spawnBountyRequirements();
    if (result.success) {
        logger.debug(`[ADMIN UI] Spawned ${result.itemsSpawned} items for bounty contracts`);
        refreshAdminUI();
    } else {
        logger.debug(`[ADMIN UI] ${result.reason}`);
    }
}

function clearBountyContracts() {
    const result = adminPanel.clearBountyContracts();
    if (result.success) {
        logger.debug(`[ADMIN UI] Cleared ${result.count} bounty contracts`);
        refreshAdminUI();
    }
}

// Corruption handlers
function triggerCorruption(durationSeconds) {
    const result = adminPanel.triggerCorruption(durationSeconds);
    if (result.success) {
        logger.debug(`[ADMIN UI] Triggered corruption for ${result.duration}s`);
        refreshAdminUI();
    }
}

function endCorruption() {
    const result = adminPanel.endCorruption();
    if (result.success) {
        logger.debug('[ADMIN UI] Ended corruption');
        refreshAdminUI();
    }
}

function setCorruptionProcChance(chance) {
    const result = adminPanel.setCorruptionProcChance(chance);
    if (result.success) {
        logger.debug(`[ADMIN UI] Set corruption proc chance to ${chance === null ? 'default' : (chance * 100) + '%'}`);
        refreshAdminUI();
    }
}

// Gold handlers
function addGold(amount) {
    const result = adminPanel.addGold(amount);
    if (result.success) {
        logger.debug(`[ADMIN UI] Added ${amount} gold`);
        refreshAdminUI();
    }
}

function addGoldCustom() {
    const input = document.getElementById('admin-gold-input');
    const amount = parseInt(input.value, 10);
    if (!isNaN(amount) && amount > 0) {
        addGold(amount);
    }
}

function setGoldCustom() {
    const input = document.getElementById('admin-gold-input');
    const amount = parseInt(input.value, 10);
    if (!isNaN(amount) && amount >= 0) {
        const result = adminPanel.setGold(amount);
        if (result.success) {
            logger.debug(`[ADMIN UI] Set gold to ${amount}`);
            refreshAdminUI();
        }
    }
}

// Quest handlers
function completeSelectedQuest() {
    const select = document.getElementById('admin-quest-select');
    if (!select) return;

    const questNumber = parseInt(select.value, 10);
    if (isNaN(questNumber)) return;

    const result = gameState.forceCompleteQuest(questNumber);
    if (result.success) {
        const quest = QUESTS[questNumber];
        toastSuccess(`Quest completed: ${quest?.name || questNumber}`);
        logger.debug(`[ADMIN UI] Force completed quest ${questNumber}`);
        refreshAdminUI();
    } else {
        logger.debug(`[ADMIN UI] Quest completion failed: ${result.reason}`);
    }
}

function completeAllQuests() {
    const totalQuests = getTotalQuestCount();
    let completed = 0;

    for (let i = 1; i <= totalQuests; i++) {
        const result = gameState.forceCompleteQuest(i);
        if (result.success) completed++;
    }

    toastSuccess(`Completed ${completed} quests`);
    logger.debug(`[ADMIN UI] Force completed ${completed} quests`);
    refreshAdminUI();
}

function resetAllQuests() {
    // Clear completed quests array
    gameState.player.quests.completed = [];
    // Clear active quest
    gameState.player.quests.active = null;
    // Clear unlocks (rewards from quests)
    gameState.player.quests.unlocks = [];

    toastSuccess('All quests reset');
    logger.debug('[ADMIN UI] Reset all quests');
    refreshAdminUI();
}

// Item spawn handlers
function onItemCategoryChange() {
    const select = document.getElementById('admin-item-category');
    if (select) {
        uiState.selectedItemCategory = select.value;
        uiState.itemSearchFilter = ''; // Clear search when category changes
        refreshAdminUI();
    }
}

function onItemSearchChange() {
    const input = document.getElementById('admin-item-search');
    if (input) {
        uiState.itemSearchFilter = input.value;
        // Debounced refresh - update the dropdown without full re-render
        updateItemDropdown();
    }
}

// Update only the item dropdown without full re-render (for search filtering)
function updateItemDropdown() {
    const select = document.getElementById('admin-item-select');
    if (!select) return;

    const selectedCategory = uiState.selectedItemCategory || 'all';
    const allItems = getAllItems();

    // Filter items by category
    let filteredItems = selectedCategory === 'all'
        ? allItems
        : allItems.filter(item => item.category === selectedCategory);

    // Apply search filter
    const searchFilter = (uiState.itemSearchFilter || '').toLowerCase().trim();
    if (searchFilter) {
        filteredItems = filteredItems.filter(item =>
            item.name.toLowerCase().includes(searchFilter) ||
            item.id.toLowerCase().includes(searchFilter)
        );
    }

    // Sort items alphabetically by name
    filteredItems.sort((a, b) => a.name.localeCompare(b.name));

    // Build new options (use category icon since <option> can't render HTML sprites)
    const itemOptions = filteredItems.map(item => {
        const icon = getCategoryIcon(item.category);
        return `<option value="${item.id}">${icon} ${item.name}</option>`;
    }).join('');

    select.innerHTML = itemOptions;
}

function getSelectedItem() {
    const select = document.getElementById('admin-item-select');
    return select ? select.value : null;
}

function getItemQuantity() {
    const input = document.getElementById('admin-item-qty');
    const qty = parseInt(input?.value, 10);
    return !isNaN(qty) && qty > 0 ? qty : 1;
}

function addItemToInventory() {
    const itemId = getSelectedItem();
    const quantity = getItemQuantity();

    if (!itemId) {
        logger.warn('[ADMIN UI] No item selected');
        return;
    }

    const result = gameState.addItem(itemId, quantity);
    if (result.success) {
        logger.debug(`[ADMIN UI] Added ${quantity}x ${itemId} to inventory`);
    } else {
        logger.warn(`[ADMIN UI] Failed to add item: ${result.reason}`);
    }
}

function addItemToBank() {
    const itemId = getSelectedItem();
    const quantity = getItemQuantity();

    if (!itemId) {
        logger.warn('[ADMIN UI] No item selected');
        return;
    }

    const result = gameState.addBankItem(itemId, quantity);
    if (result.success) {
        logger.debug(`[ADMIN UI] Added ${quantity}x ${itemId} to bank`);
    } else {
        logger.warn(`[ADMIN UI] Failed to add item to bank: ${result.reason}`);
    }
}

function addItemQty(quantity) {
    const itemId = getSelectedItem();

    if (!itemId) {
        logger.warn('[ADMIN UI] No item selected');
        return;
    }

    const result = gameState.addItem(itemId, quantity);
    if (result.success) {
        logger.debug(`[ADMIN UI] Added ${quantity}x ${itemId} to inventory`);
    } else {
        logger.warn(`[ADMIN UI] Failed to add item: ${result.reason}`);
    }
}

// Tool purchase handlers
function resetSkillTools() {
    const skillId = getSelectedSkill();
    const tools = getToolsForSkill(skillId);
    if (tools.length === 0) {
        logger.warn('[ADMIN UI] No tools for skill:', skillId);
        return;
    }

    const result = adminPanel.resetPurchasedTools(skillId);
    if (result.success) {
        logger.debug(`[ADMIN UI] Reset purchased tools for ${skillId}`);
        refreshAdminUI();
    }
}

function grantAllSkillTools() {
    const skillId = getSelectedSkill();
    const tools = getToolsForSkill(skillId);
    if (tools.length === 0) {
        logger.warn('[ADMIN UI] No tools for skill:', skillId);
        return;
    }

    const result = adminPanel.grantAllTools(skillId, tools);
    if (result.success) {
        logger.debug(`[ADMIN UI] Granted all tools for ${skillId}`);
        refreshAdminUI();
    }
}

function setGlobalSpeed(multiplier) {
    adminPanel.setGlobalSpeed(multiplier);
    refreshAdminUI();
}

function setCustomSpeed() {
    const input = document.getElementById('admin-speed-input');
    const speed = parseFloat(input.value);
    if (!isNaN(speed) && speed >= 1) {
        adminPanel.setGlobalSpeed(speed);
        refreshAdminUI();
    }
}

function setGlobalXpMultiplier(multiplier) {
    adminPanel.setGlobalXpMultiplier(multiplier);
    refreshAdminUI();
}

function setCustomXpMultiplier() {
    const input = document.getElementById('admin-xp-mult-input');
    const mult = parseFloat(input.value);
    if (!isNaN(mult) && mult > 0) {
        adminPanel.setGlobalXpMultiplier(mult);
        refreshAdminUI();
    }
}

function setNodeXpOverride() {
    const skillId = getSelectedSkill();
    const nodeIdSelect = document.getElementById('admin-node-id');
    const xpInput = document.getElementById('admin-node-xp');
    const nodeId = nodeIdSelect.value;
    const xp = parseInt(xpInput.value, 10);

    if (nodeId && !isNaN(xp)) {
        adminPanel.setNodeXpOverride(skillId, nodeId, xp);
        refreshAdminUI();
    }
}

function clearNodeXpOverride() {
    const skillId = getSelectedSkill();
    const nodeIdSelect = document.getElementById('admin-node-id');
    const nodeId = nodeIdSelect.value;

    if (nodeId) {
        adminPanel.clearNodeXpOverride(skillId, nodeId);
        refreshAdminUI();
    }
}

function removeNodeOverride(key) {
    // Key is in format "skillId:nodeId"
    const [skillId, nodeId] = key.split(':');
    adminPanel.clearNodeXpOverride(skillId, nodeId);
    refreshAdminUI();
}

function setToolSpeedOverride() {
    const toolIdSelect = document.getElementById('admin-tool-id');
    const speedInput = document.getElementById('admin-tool-speed');
    const toolId = toolIdSelect.value;
    const speed = parseFloat(speedInput.value);

    if (toolId && !isNaN(speed)) {
        adminPanel.setToolSpeedOverride(toolId, speed);
        refreshAdminUI();
    }
}

function clearToolSpeedOverride() {
    const toolIdSelect = document.getElementById('admin-tool-id');
    const toolId = toolIdSelect.value;

    if (toolId) {
        adminPanel.clearToolSpeedOverride(toolId);
        refreshAdminUI();
    }
}

function removeToolOverride(toolId) {
    adminPanel.clearToolSpeedOverride(toolId);
    refreshAdminUI();
}

function resetOverrides() {
    adminPanel.resetOverrides();
    refreshAdminUI();
}

function confirmResetCharacter() {
    if (confirm('Are you sure you want to reset your character? This will reset all skills to level 1 and clear your inventory.')) {
        adminPanel.resetCharacter();
        refreshAdminUI();
    }
}

export function initAdminUI() {
    logger.debug('[ADMIN UI] Initialized');
}

export function updateAdminUI() {
    // Only update if admin panel is currently visible
    const adminInterface = document.querySelector('.admin-interface');
    if (adminInterface) {
        // Could add live updates here if needed
    }
}

// Handle skill selection change - update node/tool dropdowns
function onSkillChange() {
    const skillSelect = document.getElementById('admin-skill-select');
    if (skillSelect) {
        uiState.selectedSkill = skillSelect.value;
        // Re-render to update node/tool dropdowns for the new skill
        refreshAdminUI();
    }
}

// ==================== MILESTONE UNLOCK HANDLERS ====================

function toggleMilestone(milestoneId) {
    const arch = gameState.initializeArchitecting();
    const index = arch.completedMilestones.indexOf(milestoneId);

    if (index === -1) {
        // Unlock it
        arch.completedMilestones.push(milestoneId);
        logger.debug(`[ADMIN] Unlocked milestone: ${milestoneId}`);
    } else {
        // Remove it
        arch.completedMilestones.splice(index, 1);
        logger.debug(`[ADMIN] Removed milestone: ${milestoneId}`);
    }

    refreshAdminUI();
}

function unlockAllMilestones() {
    const arch = gameState.initializeArchitecting();
    arch.completedMilestones = Object.values(MILESTONES).map(m => m.id);
    logger.debug('[ADMIN] Unlocked all milestones');
    refreshAdminUI();
}

function resetAllMilestones() {
    const arch = gameState.initializeArchitecting();
    arch.completedMilestones = [];
    logger.debug('[ADMIN] Reset all milestones');
    refreshAdminUI();
}

// Starter gear definitions for combat testing
const STARTER_GEAR = {
    melee: {
        set: 'melee',
        weapon: 'pyrestone_dirk',
        armor: ['pyrestone_helm', 'pyrestone_shoulders', 'pyrestone_gloves', 'pyrestone_chestplate', 'pyrestone_legguards', 'pyrestone_boots']
    },
    ranged: {
        set: 'ranged',
        weapon: 'shortbow',
        ammo: { id: 'pyrestone_arrows', quantity: 500 },
        armor: ['treated_helm', 'treated_shoulders', 'treated_gloves', 'treated_chestplate', 'treated_legguards', 'treated_boots']
    },
    magic: {
        set: 'magic',
        weapon: 'common_staff',
        glyphs: [
            { id: 'flame_glyph', quantity: 100 },
            { id: 'frost_glyph', quantity: 100 },
            { id: 'stone_glyph', quantity: 100 },
            { id: 'storm_glyph', quantity: 100 }
        ],
        armor: ['linenweave_helm', 'linenweave_shoulders', 'linenweave_gloves', 'linenweave_chestplate', 'linenweave_legguards', 'linenweave_boots']
    }
};

function equipStarterSet(style) {
    const config = STARTER_GEAR[style];
    if (!config) return;

    // Switch to the appropriate equipment set
    gameState.loadEquipmentSet(config.set);

    // Get the equipment set's gear directly
    const sets = gameState.getEquipmentSets();
    const gear = sets[config.set].gear;

    // Unequip existing gear to inventory
    const armorSlots = ['head', 'shoulders', 'hands', 'body', 'legs', 'feet'];
    const allSlots = ['weapon', ...armorSlots, 'ammo'];
    allSlots.forEach(slot => {
        if (gear[slot]) {
            // Return existing item to inventory
            if (slot === 'ammo') {
                const qty = sets[config.set].ammoQuantity || 0;
                if (qty > 0) gameState.addItem(gear[slot], qty);
                sets[config.set].ammoQuantity = 0;
            } else {
                gameState.addItem(gear[slot], 1);
            }
            gear[slot] = null;
        }
    });

    // Directly set weapon
    gear.weapon = config.weapon;

    // Directly set armor
    config.armor.forEach((itemId, index) => {
        gear[armorSlots[index]] = itemId;
    });

    // Set ammo for ranged
    if (config.ammo) {
        gear.ammo = config.ammo.id;
        sets[config.set].ammoQuantity = config.ammo.quantity;
    }

    // Add glyphs for magic (to inventory)
    if (config.glyphs) {
        config.glyphs.forEach(glyph => {
            gameState.addItem(glyph.id, glyph.quantity);
        });
    }

    toastSuccess(`Equipped starter ${style} set!`);
    renderAdminUI();
}

function equipStarterMelee() {
    equipStarterSet('melee');
}

function equipStarterRanged() {
    equipStarterSet('ranged');
}

function equipStarterMagic() {
    equipStarterSet('magic');
}

// Expose to window for onclick handlers
if (typeof window !== 'undefined') {
    window.adminUI = {
        addLevels,
        setLevel,
        setLevelTo,
        addXP,
        addXPCustom,
        addGold,
        addGoldCustom,
        setGoldCustom,
        completeSelectedQuest,
        completeAllQuests,
        resetAllQuests,
        onItemCategoryChange,
        onItemSearchChange,
        addItemToInventory,
        addItemToBank,
        addItemQty,
        skipTime,
        completeActions,
        simulateOffline,
        simulateOfflineCustom,
        skipPlotTime,
        skipPlotTimeCustom,
        completeAllPlots,
        clearAllPlots,
        unlockAllPlots,
        resetPlotLocks,
        unlockBloomhold,
        lockBloomhold,
        clearBloomhold,
        unlockPilferingGuild,
        lockPilferingGuild,
        skipContractTime,
        skipContractTimeCustom,
        completeAllContracts,
        unlockAllPets,
        lockAllPets,
        maxAllPetFeeds,
        resetAllPetFeeds,
        resetAllPetTiers,
        fillLootChest,
        clearLootChest,
        spawnBountyRequirements,
        clearBountyContracts,
        triggerCorruption,
        endCorruption,
        setCorruptionProcChance,
        setGlobalSpeed,
        setCustomSpeed,
        setGlobalXpMultiplier,
        setCustomXpMultiplier,
        setNodeXpOverride,
        clearNodeXpOverride,
        removeNodeOverride,
        setToolSpeedOverride,
        clearToolSpeedOverride,
        removeToolOverride,
        resetOverrides,
        resetSkillTools,
        grantAllSkillTools,
        confirmResetCharacter,
        onSkillChange,
        toggleMilestone,
        unlockAllMilestones,
        resetAllMilestones,
        equipStarterMelee,
        equipStarterRanged,
        equipStarterMagic
    };
}

export default {
    renderAdminUI,
    initAdminUI,
    updateAdminUI
};
